/**
 * Pinia Store: POS Newsletter
 *
 * Handles newsletter templates and sending from Point of Sale.
 */

import Vue from 'vue';
import { defineStore } from 'pinia';
import _ from 'lodash';

import NewsletterApi from '@/extensions/newsletter/store/api/newsletter';
import { useSetupStore, usePosSetupStore } from '@/stores';

export const usePosNewsletterStore = defineStore('posNewsletter', {
    state: () => ({
        loading: false,
        templates: {
            loading: false,
            items: []
        },
        sentNewsletters: {
            loading: false,
            items: [],
            pageInfo: {
                total: 0,
                perPage: 10,
                currentPage: 1,
                lastPage: 0
            }
        },
        selectedTemplate: null,
        selectedRecipients: [],
        subject: '',
        preheader: '',
        testMode: false,
        testEmail: '',
        testMember: null,
        sending: false,
        currentDraftId: null // Track the current draft for reuse in subsequent test sends
    }),

    getters: {
        hasTemplates: (state) => state.templates.items.length > 0,
        canSend: (state) => {
            if (state.testMode) {
                return !!state.testEmail && state.selectedTemplate;
            }
            return state.selectedRecipients.length > 0 && state.selectedTemplate;
        }
    },

    actions: {
        updateTemplatesLoading(value) {
            this.templates.loading = value;
        },

        insertTemplates(items) {
            this.templates.items = items;
        },

        updateSentNewslettersLoading(value) {
            this.sentNewsletters.loading = value;
        },

        insertSentNewsletters(items, pageInfo) {
            this.sentNewsletters.items = items;
            if (pageInfo) {
                this.sentNewsletters.pageInfo = _.cloneDeep(pageInfo);
            }
        },

        appendSentNewsletters(items, pageInfo) {
            this.sentNewsletters.items = [...this.sentNewsletters.items, ...items];
            if (pageInfo) {
                this.sentNewsletters.pageInfo = _.cloneDeep(pageInfo);
            }
        },

        updateNewsletterStats(newsletterId, statistics) {
            const index = this.sentNewsletters.items.findIndex(
                n => parseInt(n.id, 10) === parseInt(newsletterId, 10)
            );
            if (index === -1) return;

            const newsletter = this.sentNewsletters.items[index];

            // Use != null to check for both null and undefined (prevents null values from overwriting real data)
            if (statistics.status != null) Vue.set(newsletter, 'status', statistics.status);
            if (statistics.recipients != null) Vue.set(newsletter, 'recipients', statistics.recipients);
            if (statistics.opens != null) Vue.set(newsletter, 'opens', statistics.opens);
            if (statistics.clicks != null) Vue.set(newsletter, 'clicks', statistics.clicks);
            if (statistics.bounces != null) Vue.set(newsletter, 'bounces', statistics.bounces);
            if (statistics.received != null) Vue.set(newsletter, 'received', statistics.received);
            if (statistics.skipped != null) Vue.set(newsletter, 'skipped', statistics.skipped);
            if (statistics.started_sending_at != null) Vue.set(newsletter, 'started_sending_at', statistics.started_sending_at);
            if (statistics.finished_sending_at != null) Vue.set(newsletter, 'finished_sending_at', statistics.finished_sending_at);
            if (statistics.progress != null) {
                Vue.set(newsletter, 'progress', statistics.progress);
                // Also update statusLog if it exists
                if (newsletter.statusLog) {
                    Vue.set(newsletter.statusLog, 'progress', statistics.progress);
                }
            }
        },

        setSelectedTemplate(template) {
            this.selectedTemplate = template;
        },

        addRecipient(member) {
            const exists = this.selectedRecipients.find(r => r.id === member.id);
            if (!exists) {
                this.selectedRecipients.push(member);
            }
        },

        removeRecipient(memberId) {
            this.selectedRecipients = this.selectedRecipients.filter(r => r.id !== memberId);
        },

        clearRecipients() {
            this.selectedRecipients = [];
        },

        setTestMode(value) {
            this.testMode = value;
        },

        setTestEmail(value) {
            this.testEmail = value;
        },

        setTestMember(member) {
            this.testMember = member;
        },

        setSubject(value) {
            this.subject = value;
        },

        setPreheader(value) {
            this.preheader = value;
        },

        setSending(value) {
            this.sending = value;
        },

        reset() {
            this.selectedTemplate = this.templates.items.length > 0 ? this.templates.items[0] : null;
            this.selectedRecipients = [];
            this.subject = '';
            this.preheader = '';
            this.testMode = false;
            this.testEmail = '';
            this.testMember = null;
            this.currentDraftId = null;
        },

        setCurrentDraftId(id) {
            this.currentDraftId = id;
        },

        clearDraft() {
            this.currentDraftId = null;
        },

        /**
         * Fetch POS newsletter templates for the current CMS
         */
        fetchTemplates() {
            const setupStore = useSetupStore();
            const posSetupStore = usePosSetupStore();
            const cmsId = _.get(setupStore, 'activeCms.id');
            const posId = _.get(posSetupStore, 'activePointOfSale.id');

            if (!cmsId) {
                return Promise.resolve([]);
            }

            this.updateTemplatesLoading(true);

            const filters = [
                { key: 'cms', match: parseInt(cmsId, 10) },
                { key: 'is_pos_template', match: true }
            ];

            if (posId) {
                filters.push({ key: 'accessible_by_pos_id', match: parseInt(posId, 10) });
            }

            return NewsletterApi.fetchNewslettersPaginated({
                pageInfo: { currentPage: 1, perPage: 100 },
                variables: { order: 'subject', direction: 'asc' },
                filters
            })
                .then(data => {
                    this.insertTemplates(data.items || []);
                    // Auto-select first template if available
                    if (data.items && data.items.length > 0 && !this.selectedTemplate) {
                        this.setSelectedTemplate(data.items[0]);
                    }
                    return data.items;
                })
                .catch(err => {
                    console.error('Error fetching POS newsletter templates:', err);
                    this.insertTemplates([]);
                    throw err;
                })
                .finally(() => {
                    this.updateTemplatesLoading(false);
                });
        },

        /**
         * Fetch newsletters sent from a specific POS
         * @param {number} posId - POS ID
         * @param {number} currentPage - Page number (default 1)
         * @param {number} perPage - Items per page (default 10)
         * @param {boolean} append - Whether to append to existing items (for infinite scroll)
         */
        fetchSentNewsletters(posId, currentPage = 1, perPage = 10, append = false) {
            const setupStore = useSetupStore();
            const cmsId = _.get(setupStore, 'activeCms.id');

            if (!cmsId || !posId) {
                return Promise.resolve({ items: [], pageInfo: {} });
            }

            this.updateSentNewslettersLoading(true);

            const filters = [
                { key: 'cms', match: parseInt(cmsId, 10) },
                { key: 'sent_from_pos_id', match: parseInt(posId, 10) }
                // Show all statuses - drafts will be displayed with a test/draft indicator
            ];

            return NewsletterApi.fetchNewslettersPaginated({
                pageInfo: { currentPage, perPage },
                variables: { order: 'created_at', direction: 'desc' },
                filters
            })
                .then(data => {
                    if (append && currentPage > 1) {
                        this.appendSentNewsletters(data.items || [], data.pageInfo);
                    } else {
                        this.insertSentNewsletters(data.items || [], data.pageInfo);
                    }
                    return data;
                })
                .catch(err => {
                    console.error('Error fetching sent newsletters:', err);
                    if (!append) {
                        this.insertSentNewsletters([], null);
                    }
                    throw err;
                })
                .finally(() => {
                    this.updateSentNewslettersLoading(false);
                });
        },

        /**
         * Fetch newsletter preview HTML from the first sent email
         */
        fetchNewsletterPreview(newsletterId) {
            return NewsletterApi.fetchNewsletterPreview(newsletterId);
        },

        /**
         * Send newsletter from POS
         */
        send(articleIds = []) {
            const posSetupStore = usePosSetupStore();
            const posId = _.get(posSetupStore, 'activePointOfSale.id');

            if (!this.selectedTemplate) {
                return Promise.reject(new Error('No template selected'));
            }

            this.setSending(true);

            const variables = {
                id: parseInt(this.selectedTemplate.id, 10),
                posId: posId ? parseInt(posId, 10) : null,
                articleIds: articleIds.map(id => parseInt(id, 10)),
                subject: this.subject || null,
                preheader: this.preheader || null
            };

            // Reuse existing draft if available
            if (this.currentDraftId) {
                variables.draftId = parseInt(this.currentDraftId, 10);
            }

            if (this.testMode) {
                variables.mode = 'test';
                variables.testRecipients = [this.testEmail];
                if (this.testMember) {
                    variables.testMember = { id: parseInt(this.testMember.id, 10) };
                }
            } else {
                variables.mode = 'production';
                variables.recipientMemberIds = this.selectedRecipients.map(r => parseInt(r.id, 10));
            }

            return NewsletterApi.send(variables)
                .then(newsletter => {
                    // Store draft ID for reuse in subsequent test sends
                    if (this.testMode && newsletter && newsletter.id) {
                        this.setCurrentDraftId(newsletter.id);
                    } else {
                        // Clear draft after production send
                        this.clearDraft();
                    }

                    // Refresh sent newsletters list
                    if (posId) {
                        this.fetchSentNewsletters(posId);
                    }
                    return newsletter;
                })
                .catch(err => {
                    console.error('Error sending newsletter:', err);
                    throw err;
                })
                .finally(() => {
                    this.setSending(false);
                });
        }
    }
});
